import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CHAT_V2_MAX_INPUT_CHARACTERS,
    getChatV2ErrorMessage,
} from '../services/chatV2';
import {
    homeChatSuggestionIdentity,
    homeSuggestionOffsetsAfterInsertingReplacement,
    normalizeHomeChatSuggestion,
    preservedHomeDefaultSuggestionOffset,
    preservedHomePersonalSuggestionOffset,
    removeMatchingHomePersonalSuggestion,
    replaceDismissedHomeSuggestion,
    resolveDismissedPublicExampleKeys,
    resolveHomePersonalSuggestions,
    sanitizeDismissedPublicExampleKeys,
    selectHomeChatSuggestions,
} from '../utils/homeChatSuggestions';
import { CHAT_SUGGESTIONS_CHANGED, getDefaultChatAdapter } from '../services/chatThreads';

const SUGGESTION_REFRESH_COOLDOWN_MS = 60_000;

const SUGGESTION_VISIT_OFFSETS_STORAGE_KEY = 'home-chat-suggestion-visit-offsets';
const SIGNED_OUT_DISMISSED_PUBLIC_EXAMPLES_STORAGE_KEY =
    'home-chat-signed-out-dismissed-public-example-keys';

const homeSuggestionVisitOffsets = new Map();
let hasHydratedHomeSuggestionVisitOffsets = false;
let signedOutDismissedPublicExampleKeys = [];
let hasHydratedSignedOutDismissedPublicExampleKeys = false;

const hydrateHomeSuggestionVisitOffsets = () => {
    if (hasHydratedHomeSuggestionVisitOffsets) {
        return;
    }
    hasHydratedHomeSuggestionVisitOffsets = true;

    try {
        const storedOffsets = JSON.parse(
            window.sessionStorage.getItem(SUGGESTION_VISIT_OFFSETS_STORAGE_KEY) || '{}',
        );
        Object.entries(storedOffsets).forEach(([accountKey, offsets]) => {
            if (!offsets || typeof offsets !== 'object') {
                return;
            }

            homeSuggestionVisitOffsets.set(accountKey, {
                personalOffset: Number.isFinite(offsets.personalOffset)
                    ? offsets.personalOffset
                    : 0,
                defaultOffset: Number.isFinite(offsets.defaultOffset)
                    ? offsets.defaultOffset
                    : 0,
            });
        });
    } catch (_) {
        // Session storage can be unavailable in privacy-restricted browsers.
    }
};

const getHomeSuggestionVisitOffsets = (accountKey) => {
    hydrateHomeSuggestionVisitOffsets();
    return homeSuggestionVisitOffsets.get(accountKey) || {
        personalOffset: 0,
        defaultOffset: 0,
    };
};

const saveHomeSuggestionVisitOffsets = (accountKey, offsets) => {
    hydrateHomeSuggestionVisitOffsets();
    homeSuggestionVisitOffsets.set(accountKey, {
        personalOffset: offsets.personalOffset,
        defaultOffset: offsets.defaultOffset,
    });

    try {
        window.sessionStorage.setItem(
            SUGGESTION_VISIT_OFFSETS_STORAGE_KEY,
            JSON.stringify(Object.fromEntries(homeSuggestionVisitOffsets)),
        );
    } catch (_) {
        // The in-memory cursor still rotates suggestions for this page session.
    }
};

const getSignedOutDismissedPublicExampleKeys = () => {
    if (!hasHydratedSignedOutDismissedPublicExampleKeys) {
        hasHydratedSignedOutDismissedPublicExampleKeys = true;
        try {
            signedOutDismissedPublicExampleKeys = sanitizeDismissedPublicExampleKeys(
                JSON.parse(
                    window.sessionStorage.getItem(
                        SIGNED_OUT_DISMISSED_PUBLIC_EXAMPLES_STORAGE_KEY,
                    ) || '[]',
                ),
            );
        } catch (_) {
            signedOutDismissedPublicExampleKeys = [];
        }
    }

    return signedOutDismissedPublicExampleKeys;
};

const saveSignedOutDismissedPublicExampleKeys = (keys) => {
    signedOutDismissedPublicExampleKeys = sanitizeDismissedPublicExampleKeys(keys);
    hasHydratedSignedOutDismissedPublicExampleKeys = true;
    try {
        window.sessionStorage.setItem(
            SIGNED_OUT_DISMISSED_PUBLIC_EXAMPLES_STORAGE_KEY,
            JSON.stringify(signedOutDismissedPublicExampleKeys),
        );
    } catch (_) {
        // The in-memory set still suppresses examples for this mounted browser session.
    }

    return signedOutDismissedPublicExampleKeys;
};

const clearSignedOutDismissedPublicExampleKeys = () => {
    signedOutDismissedPublicExampleKeys = [];
    hasHydratedSignedOutDismissedPublicExampleKeys = true;
    try {
        window.sessionStorage.removeItem(
            SIGNED_OUT_DISMISSED_PUBLIC_EXAMPLES_STORAGE_KEY,
        );
    } catch (_) {
        // There is no persisted guest state to clear when session storage is unavailable.
    }
};

export const resetHomeSuggestionVisitOffsets = () => {
    homeSuggestionVisitOffsets.clear();
    hasHydratedHomeSuggestionVisitOffsets = false;
    try {
        window.sessionStorage.removeItem(SUGGESTION_VISIT_OFFSETS_STORAGE_KEY);
    } catch (_) {
        // No cleanup is needed when session storage is unavailable.
    }
    signedOutDismissedPublicExampleKeys = [];
    hasHydratedSignedOutDismissedPublicExampleKeys = false;
    try {
        window.sessionStorage.removeItem(
            SIGNED_OUT_DISMISSED_PUBLIC_EXAMPLES_STORAGE_KEY,
        );
    } catch (_) {
        // No cleanup is needed when session storage is unavailable.
    }
};

const useAskController = ({ authenticated, uid }) => {
    const chat = getDefaultChatAdapter();
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const suggestionAccountKey = authenticated === true
        ? `signed-in:${typeof uid === 'string' && uid ? uid : 'resolving'}`
        : authenticated === false
            ? 'signed-out'
            : 'resolving';
    const identityResolved = authenticated === false || (authenticated === true && Boolean(uid));

    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [dismissingSuggestionIdentity, setDismissingSuggestionIdentity] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [focusedSuggestions, setFocusedSuggestions] = useState([]);
    const [personalSuggestions, setPersonalSuggestions] = useState([]);
    const [dismissedPublicExampleKeys, setDismissedPublicExampleKeys] = useState([]);
    const [publicChatExamples, setPublicChatExamples] = useState([]);
    const [publicChatExamplesLoadState, setPublicChatExamplesLoadState] = useState('loading');
    const [suggestionLoadState, setSuggestionLoadState] = useState({
        accountKey: suggestionAccountKey,
        status: authenticated === false ? 'resolved' : 'loading',
    });
    const defaultSuggestionOffsetRef = useRef(0);
    const dismissedPublicExampleKeysRef = useRef([]);
    const hasSelectedSuggestionsRef = useRef(false);
    const personalSuggestionOffsetRef = useRef(0);
    const suggestionAccountKeyRef = useRef(suggestionAccountKey);
    const visitOffsetsByAccountRef = useRef(new Map());
    const suppressedPersonalSuggestionKeysRef = useRef(new Set());
    const personalSuggestionsRef = useRef(personalSuggestions);
    const focusedSuggestionsRef = useRef(focusedSuggestions);
    const publicChatExamplesRef = useRef(publicChatExamples);
    const publicFetchRef = useRef({ pending: null, resolved: false, started: false });
    const personalFetchRef = useRef({ pending: null, lastSuccess: null, started: false });
    const suggestionMutationRevisionRef = useRef(0);
    suggestionAccountKeyRef.current = suggestionAccountKey;
    dismissedPublicExampleKeysRef.current = dismissedPublicExampleKeys;
    personalSuggestionsRef.current = personalSuggestions;
    focusedSuggestionsRef.current = focusedSuggestions;
    publicChatExamplesRef.current = publicChatExamples;

    const startRequestRef = useRef(null);
    const lifecycleRef = useRef({ accountKey: suggestionAccountKey, generation: 0, mounted: false, pending: false });
    if (lifecycleRef.current.accountKey !== suggestionAccountKey) {
        lifecycleRef.current.accountKey = suggestionAccountKey;
        lifecycleRef.current.generation += 1;
        lifecycleRef.current.pending = false;
        startRequestRef.current = null;
        suppressedPersonalSuggestionKeysRef.current.clear();
    }
    const previousAccountRef = useRef(suggestionAccountKey);
    const guestSigninDraftRef = useRef(null);
    const draftRef = useRef(text);
    draftRef.current = text;
    const consumedPrefillsRef = useRef(new Set());
    const hasResolvedIdentityRef = useRef(identityResolved);
    const initialPrefillPendingRef = useRef(false);

    useEffect(() => {
        const lifecycle = lifecycleRef.current;
        const publicFetch = publicFetchRef.current;
        lifecycle.mounted = true;
        return () => {
            lifecycle.mounted = false;
            lifecycle.generation += 1;
            lifecycle.pending = false;
            publicFetch.started = false;
            publicFetch.pending = null;
            personalFetchRef.current.pending = null;
        };
    }, []);

    useLayoutEffect(() => {
        const request = startRequestRef.current;
        if (request?.focusedAsk && request.locationKey !== location.key) {
            // Back leaves the draft in Home. A late response must not replace
            // that Home entry or pull the user back into a conversation.
            startRequestRef.current = null;
            lifecycleRef.current.pending = false;
            setSubmitting(false);
        }
    }, [location.key]);

    useLayoutEffect(() => {
        if (previousAccountRef.current === suggestionAccountKey) return;
        previousAccountRef.current = suggestionAccountKey;
        const handoff = authenticated === true && uid ? guestSigninDraftRef.current : null;
        // A direct link can be consumed before the initial login request settles.
        // Keep its draft through that initial resolution, never across a later account change.
        const preserveInitialPrefill = !hasResolvedIdentityRef.current && initialPrefillPendingRef.current;
        const nextDraft = handoff ?? (preserveInitialPrefill ? draftRef.current : '');
        setText(nextDraft);
        draftRef.current = nextDraft;
        setErrorMessage('');
        setSubmitting(false);
        setDismissingSuggestionIdentity('');
        if (identityResolved) {
            hasResolvedIdentityRef.current = true;
            initialPrefillPendingRef.current = false;
            guestSigninDraftRef.current = null;
        }
    }, [authenticated, suggestionAccountKey, uid, identityResolved]);

    useEffect(() => {
        if (location.pathname !== '/ask' || consumedPrefillsRef.current.has(location.key)) return;
        const params = new URLSearchParams(location.search);
        const replacement = typeof location.state?.askReplacementText === 'string'
            ? location.state.askReplacementText
            : params.has('q') ? params.get('q') : null;
        consumedPrefillsRef.current.add(location.key);
        if (replacement !== null) {
            if (!hasResolvedIdentityRef.current) initialPrefillPendingRef.current = true;
            const nextText = replacement.slice(0, CHAT_V2_MAX_INPUT_CHARACTERS);
            draftRef.current = nextText;
            setText(nextText);
            setErrorMessage('');
        }
    }, [location.key, location.pathname, location.search, location.state]);

    const prepareSignin = () => {
        if (authenticated === false) guestSigninDraftRef.current = draftRef.current;
    };

    const requestIsCurrent = (generation) => (
        lifecycleRef.current.mounted && lifecycleRef.current.generation === generation
    );

    const hasText = text.trim().length > 0;
    const suggestionActionIsPending = submitting || Boolean(dismissingSuggestionIdentity);
    useEffect(() => {
        personalFetchRef.current = { pending: null, lastSuccess: null, started: false };
        let visitOffsets = visitOffsetsByAccountRef.current.get(suggestionAccountKey);
        if (!visitOffsets) {
            visitOffsets = getHomeSuggestionVisitOffsets(suggestionAccountKey);
            visitOffsetsByAccountRef.current.set(suggestionAccountKey, visitOffsets);
        }

        personalSuggestionOffsetRef.current = visitOffsets.personalOffset;
        defaultSuggestionOffsetRef.current = visitOffsets.defaultOffset;
        hasSelectedSuggestionsRef.current = false;
        setFocusedSuggestions([]);
        personalSuggestionsRef.current = [];
        setPersonalSuggestions([]);
        setDismissingSuggestionIdentity('');
        if (authenticated === true) {
            clearSignedOutDismissedPublicExampleKeys();
            dismissedPublicExampleKeysRef.current = [];
            setDismissedPublicExampleKeys([]);
        } else if (authenticated === false) {
            const guestDismissedKeys = getSignedOutDismissedPublicExampleKeys();
            dismissedPublicExampleKeysRef.current = guestDismissedKeys;
            setDismissedPublicExampleKeys(guestDismissedKeys);
        } else {
            dismissedPublicExampleKeysRef.current = [];
            setDismissedPublicExampleKeys([]);
        }
        setSuggestionLoadState({
            accountKey: suggestionAccountKey,
            status: authenticated === false ? 'resolved' : 'loading',
        });
    }, [authenticated, suggestionAccountKey]);

    const [suggestionRefreshRevision, setSuggestionRefreshRevision] = useState(0);
    const suggestionInvalidationRef = useRef(0);
    useEffect(() => {
        const invalidate = () => {
            suggestionInvalidationRef.current += 1;
            personalFetchRef.current.lastSuccess = null;
            setSuggestionRefreshRevision((revision) => revision + 1);
        };
        window.addEventListener(CHAT_SUGGESTIONS_CHANGED, invalidate);
        return () => window.removeEventListener(CHAT_SUGGESTIONS_CHANGED, invalidate);
    }, []);

    // The provider survives tab changes. Refresh on Home/Ask activation instead
    // of resetting its draft, visible pair, or dismissal state on each visit.
    useEffect(() => {
        const active = ['/', '/home', '/ask'].includes(location.pathname);

        const publicFetch = publicFetchRef.current;
        if ((active || !publicFetch.started) && !publicFetch.pending && !publicFetch.resolved) {
            const request = {};
            publicFetch.started = true;
            publicFetch.pending = request;
            const isCurrent = () => lifecycleRef.current.mounted && publicFetch.pending === request;
            chat.getPublicExamples()
                .then((manifest) => {
                    if (!isCurrent()) return;
                    const examples = manifest.examples.map((example) => ({
                        ...example,
                        publicExamplesRevision: manifest.revision,
                    }));
                    publicChatExamplesRef.current = examples;
                    setPublicChatExamples(examples);
                    publicFetch.resolved = true;
                    setPublicChatExamplesLoadState('resolved');
                })
                .catch(() => {
                    if (!isCurrent()) return;
                    // A later Home/Ask activation can retry the initial load.
                    setPublicChatExamplesLoadState('error');
                })
                .finally(() => {
                    if (publicFetch.pending === request) publicFetch.pending = null;
                });
        }

        const personalFetch = personalFetchRef.current;
        if ((!active && personalFetch.started) || authenticated !== true || !uid || personalFetch.pending || lifecycleRef.current.pending ||
            (personalFetch.lastSuccess !== null &&
                Date.now() - personalFetch.lastSuccess < SUGGESTION_REFRESH_COOLDOWN_MS)) return;

        const generation = lifecycleRef.current.generation;
        const mutationRevision = suggestionMutationRevisionRef.current;
        const invalidationRevision = suggestionInvalidationRef.current;
        const request = {};
        personalFetch.started = true;
        personalFetch.pending = request;
        const isCurrent = () => lifecycleRef.current.mounted &&
            lifecycleRef.current.generation === generation &&
            suggestionAccountKeyRef.current === suggestionAccountKey &&
            personalFetchRef.current === personalFetch && personalFetch.pending === request &&
            suggestionMutationRevisionRef.current === mutationRevision;

        chat.getSuggestions()
            .then((response) => {
                if (!isCurrent()) return;
                const previousPersonalSuggestions = personalSuggestionsRef.current;
                const nextPersonalSuggestions = resolveHomePersonalSuggestions(response).filter((suggestion) => (
                    !suppressedPersonalSuggestionKeysRef.current.has(normalizeHomeChatSuggestion(suggestion.text))
                ));
                const previousDismissedKeys = dismissedPublicExampleKeysRef.current;
                const nextDismissedKeys = sanitizeDismissedPublicExampleKeys([
                    ...previousDismissedKeys,
                    ...resolveDismissedPublicExampleKeys(response),
                ]);
                if (hasSelectedSuggestionsRef.current) {
                    personalSuggestionOffsetRef.current = preservedHomePersonalSuggestionOffset({
                        previousSuggestions: previousPersonalSuggestions,
                        nextSuggestions: nextPersonalSuggestions,
                        previousOffset: personalSuggestionOffsetRef.current,
                    });
                    defaultSuggestionOffsetRef.current = preservedHomeDefaultSuggestionOffset({
                        previousPersonalSuggestions,
                        nextPersonalSuggestions,
                        defaults: publicChatExamplesRef.current,
                        previousDismissedPublicExampleKeys: previousDismissedKeys,
                        nextDismissedPublicExampleKeys: nextDismissedKeys,
                        previousOffset: defaultSuggestionOffsetRef.current,
                    });
                }
                personalSuggestionsRef.current = nextPersonalSuggestions;
                setPersonalSuggestions(nextPersonalSuggestions);
                dismissedPublicExampleKeysRef.current = nextDismissedKeys;
                setDismissedPublicExampleKeys(nextDismissedKeys);
                // Keep established rows except public examples the server has
                // explicitly dismissed (for example, from another device).
                setFocusedSuggestions((current) => {
                    let next = current;
                    current.filter((suggestion) => suggestion.kind === 'default' &&
                        nextDismissedKeys.includes(suggestion.publicExampleKey)).forEach((suggestion) => {
                        const index = next.indexOf(suggestion);
                        const previousLength = next.length;
                        next = replaceDismissedHomeSuggestion({
                            visibleSuggestions: next,
                            dismissedSuggestion: suggestion,
                            personalSuggestions: nextPersonalSuggestions,
                            defaults: publicChatExamplesRef.current,
                            dismissedPublicExampleKeys: nextDismissedKeys,
                            personalOffset: personalSuggestionOffsetRef.current,
                            defaultOffset: defaultSuggestionOffsetRef.current,
                        });
                        const offsets = homeSuggestionOffsetsAfterInsertingReplacement({
                            personalSuggestions: nextPersonalSuggestions,
                            insertedSuggestion: next.length === previousLength ? next[index] : null,
                            personalOffset: personalSuggestionOffsetRef.current,
                            defaultOffset: defaultSuggestionOffsetRef.current,
                            defaults: publicChatExamplesRef.current,
                            dismissedPublicExampleKeys: nextDismissedKeys,
                        });
                        personalSuggestionOffsetRef.current = offsets.personalOffset;
                        defaultSuggestionOffsetRef.current = offsets.defaultOffset;
                        saveHomeSuggestionVisitOffsets(suggestionAccountKey, offsets);
                    });
                    if (next !== current) {
                        focusedSuggestionsRef.current = next;
                        hasSelectedSuggestionsRef.current = next.length > 0;
                    }
                    return next;
                });
                personalFetch.lastSuccess = Date.now();
                setSuggestionLoadState({ accountKey: suggestionAccountKey, status: 'resolved' });
            })
            .catch(() => {
                if (!isCurrent()) return;
                // Resolve initial loading so public examples can be used. Do
                // not clear an established pool or rotate the visible pair.
                setSuggestionLoadState({ accountKey: suggestionAccountKey, status: 'resolved' });
            })
            .finally(() => {
                if (personalFetch.pending !== request) return;
                personalFetch.pending = null;
                if (lifecycleRef.current.mounted && personalFetchRef.current === personalFetch &&
                    suggestionInvalidationRef.current !== invalidationRevision) {
                    personalFetch.lastSuccess = null;
                    setSuggestionRefreshRevision((revision) => revision + 1);
                }
            });
    }, [chat, authenticated, suggestionAccountKey, uid, location.key, location.pathname, suggestionRefreshRevision]);

    useEffect(() => {
        if (
            suggestionLoadState.accountKey !== suggestionAccountKey ||
            suggestionLoadState.status !== 'resolved' ||
            (
                publicChatExamplesLoadState === 'loading' &&
                (authenticated === false || personalSuggestions.length === 0)
            ) ||
            hasSelectedSuggestionsRef.current
        ) {
            return;
        }

        const selection = selectHomeChatSuggestions({
            personalSuggestions,
            defaults: publicChatExamples,
            dismissedPublicExampleKeys,
            personalOffset: personalSuggestionOffsetRef.current,
            defaultOffset: defaultSuggestionOffsetRef.current,
        });
        personalSuggestionOffsetRef.current = selection.nextPersonalOffset;
        defaultSuggestionOffsetRef.current = selection.nextDefaultOffset;
        saveHomeSuggestionVisitOffsets(suggestionAccountKey, {
            personalOffset: selection.nextPersonalOffset,
            defaultOffset: selection.nextDefaultOffset,
        });
        hasSelectedSuggestionsRef.current = selection.suggestions.length > 0;
        setFocusedSuggestions(selection.suggestions);
    }, [
        personalSuggestions,
        dismissedPublicExampleKeys,
        publicChatExamples,
        publicChatExamplesLoadState,
        authenticated,
        suggestionAccountKey,
        suggestionLoadState,
    ]);

    const openConversation = (path, state) => {
        const replacesAsk = location.pathname === '/ask';
        navigate(path, {
            replace: replacesAsk,
            state: replacesAsk && location.state?.askOrigin
                ? { ...state, askOrigin: location.state.askOrigin }
                : state,
        });
    };

    const startThread = async (queryText, { preserveDraft = false } = {}) => {
        const query = String(queryText || '').trim();
        if (!query || query.length > CHAT_V2_MAX_INPUT_CHARACTERS || lifecycleRef.current.pending) return;

        const generation = lifecycleRef.current.generation;
        const submittedDraft = draftRef.current;
        const request = { locationKey: location.key, focusedAsk: location.pathname === '/ask' };
        startRequestRef.current = request;
        const isCurrent = () => startRequestRef.current === request && requestIsCurrent(generation);
        lifecycleRef.current.pending = true;

        setSubmitting(true);
        setErrorMessage('');

        try {
            const runEnvelope = await chat.start(query);

            if (!isCurrent()) return;

            suppressedPersonalSuggestionKeysRef.current.add(normalizeHomeChatSuggestion(query));
            const nextPersonalSuggestions = removeMatchingHomePersonalSuggestion(personalSuggestionsRef.current, query);
            personalSuggestionsRef.current = nextPersonalSuggestions;
            setPersonalSuggestions(nextPersonalSuggestions);
            const executedSuggestion = focusedSuggestionsRef.current.find((suggestion) => (
                suggestion.kind === 'personal' &&
                normalizeHomeChatSuggestion(suggestion.text) === normalizeHomeChatSuggestion(query)
            ));
            if (executedSuggestion) {
                replaceFocusedSuggestion({
                    dismissedSuggestion: executedSuggestion,
                    nextDefaultOffset: defaultSuggestionOffsetRef.current,
                    nextDismissedPublicExampleKeys: dismissedPublicExampleKeysRef.current,
                    nextPersonalOffset: personalSuggestionOffsetRef.current,
                    nextPersonalSuggestions,
                });
            }

            openConversation(chat.threadPath(runEnvelope.threadId), {
                initialQuery: query,
                initialRun: runEnvelope,
            });

            if (!preserveDraft && draftRef.current === submittedDraft) {
                draftRef.current = '';
                setText('');
            }
        } catch (err) {
            if (!isCurrent()) return;
            setErrorMessage(
                getChatV2ErrorMessage(
                    err && err.response && err.response.data
                        ? err.response.data.error
                        : err,
                ) || 'Something went wrong.',
            );
        } finally {
            if (isCurrent()) {
                startRequestRef.current = null;
                lifecycleRef.current.pending = false;
                setSubmitting(false);
            }
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!hasText) return;
        startThread(text);
    };

    const onChange = (e) => {
        const nextText = e.target.value.slice(0, CHAT_V2_MAX_INPUT_CHARACTERS);
        if (errorMessage) {
            setErrorMessage('');
        }
        draftRef.current = nextText;
        setText(nextText);
    };

    const chatClick = (chatId) => {
        logEvent(analytics, 'chat_click', { chatId });
        openConversation(chat.publicPath(chatId));
    };

    const pastChatsClick = () => {
        navigate('/chat_threads');
    };

    const selectFocusedSuggestion = (suggestion) => {
        if (lifecycleRef.current.pending) {
            return;
        }

        if (suggestion.kind === 'default') {
            chatClick(suggestion.threadId);
            return;
        }

        startThread(suggestion.text, { preserveDraft: true });
    };

    const replaceFocusedSuggestion = ({
        dismissedSuggestion,
        nextDefaultOffset,
        nextDismissedPublicExampleKeys,
        nextPersonalOffset,
        nextPersonalSuggestions,
    }) => {
        setFocusedSuggestions((currentSuggestions) => {
            const dismissedIdentity = homeChatSuggestionIdentity(dismissedSuggestion);
            const dismissedIndex = currentSuggestions.findIndex((currentSuggestion) => (
                currentSuggestion === dismissedSuggestion ||
                homeChatSuggestionIdentity(currentSuggestion) === dismissedIdentity
            ));
            const nextVisibleSuggestions = replaceDismissedHomeSuggestion({
                visibleSuggestions: currentSuggestions,
                dismissedSuggestion,
                personalSuggestions: nextPersonalSuggestions,
                defaults: publicChatExamplesRef.current,
                dismissedPublicExampleKeys: nextDismissedPublicExampleKeys,
                personalOffset: nextPersonalOffset,
                defaultOffset: nextDefaultOffset,
            });
            const personalSuggestionsByKey = new Map(
                nextPersonalSuggestions.map((personalSuggestion) => [
                    normalizeHomeChatSuggestion(personalSuggestion.text),
                    personalSuggestion,
                ]),
            );
            const reconciledVisibleSuggestions = nextVisibleSuggestions.map((visibleSuggestion) => {
                if (visibleSuggestion.kind !== 'personal') {
                    return visibleSuggestion;
                }

                const canonicalSuggestion = personalSuggestionsByKey.get(
                    normalizeHomeChatSuggestion(visibleSuggestion.text),
                );
                return canonicalSuggestion ? {
                    ...visibleSuggestion,
                    emoji: canonicalSuggestion.emoji,
                    text: canonicalSuggestion.text,
                } : visibleSuggestion;
            });
            const insertedSuggestion = (
                dismissedIndex >= 0 &&
                reconciledVisibleSuggestions.length === currentSuggestions.length
            ) ? reconciledVisibleSuggestions[dismissedIndex] : null;
            const nextOffsets = homeSuggestionOffsetsAfterInsertingReplacement({
                personalSuggestions: nextPersonalSuggestions,
                insertedSuggestion,
                personalOffset: nextPersonalOffset,
                defaultOffset: nextDefaultOffset,
                defaults: publicChatExamplesRef.current,
                dismissedPublicExampleKeys: nextDismissedPublicExampleKeys,
            });
            personalSuggestionOffsetRef.current = nextOffsets.personalOffset;
            defaultSuggestionOffsetRef.current = nextOffsets.defaultOffset;
            saveHomeSuggestionVisitOffsets(
                suggestionAccountKeyRef.current,
                nextOffsets,
            );
            focusedSuggestionsRef.current = reconciledVisibleSuggestions;
            hasSelectedSuggestionsRef.current = reconciledVisibleSuggestions.length > 0;
            return reconciledVisibleSuggestions;
        });
    };

    const dismissFocusedSuggestion = async (suggestion) => {
        const suggestionIdentity = homeChatSuggestionIdentity(suggestion);
        if (!suggestionIdentity || lifecycleRef.current.pending) {
            return;
        }

        if (suggestion.kind === 'default' && authenticated === false) {
            const previousDismissedKeys = dismissedPublicExampleKeysRef.current;
            const nextDismissedKeys = saveSignedOutDismissedPublicExampleKeys([
                ...previousDismissedKeys,
                suggestion.publicExampleKey,
            ]);
            const nextDefaultOffset = preservedHomeDefaultSuggestionOffset({
                previousPersonalSuggestions: personalSuggestions,
                nextPersonalSuggestions: personalSuggestions,
                defaults: publicChatExamplesRef.current,
                previousDismissedPublicExampleKeys: previousDismissedKeys,
                nextDismissedPublicExampleKeys: nextDismissedKeys,
                previousOffset: defaultSuggestionOffsetRef.current,
            });
            dismissedPublicExampleKeysRef.current = nextDismissedKeys;
            setDismissedPublicExampleKeys(nextDismissedKeys);
            setErrorMessage('');
            replaceFocusedSuggestion({
                dismissedSuggestion: suggestion,
                nextDefaultOffset,
                nextDismissedPublicExampleKeys: nextDismissedKeys,
                nextPersonalOffset: personalSuggestionOffsetRef.current,
                nextPersonalSuggestions: personalSuggestions,
            });
            return;
        }

        if (authenticated !== true) {
            return;
        }

        const requestAccountKey = suggestionAccountKeyRef.current;
        const generation = lifecycleRef.current.generation;
        lifecycleRef.current.pending = true;
        // A GET started before this mutation must not overwrite its response.
        suggestionMutationRevisionRef.current += 1;
        const dismissedKeysAtDispatch = dismissedPublicExampleKeysRef.current;
        setDismissingSuggestionIdentity(suggestionIdentity);
        setErrorMessage('');

        try {
            const response = await chat.deleteSuggestion(
                suggestion.kind === 'personal' ? suggestion.text : {
                    publicExampleKey: suggestion.publicExampleKey,
                    publicExamplesRevision: suggestion.publicExamplesRevision,
                },
            );
            if (!requestIsCurrent(generation) || suggestionAccountKeyRef.current !== requestAccountKey) {
                return;
            }

            if (suggestion.kind === 'personal') {
                suppressedPersonalSuggestionKeysRef.current.add(normalizeHomeChatSuggestion(suggestion.text));
            }
            const responsePersonalSuggestions = resolveHomePersonalSuggestions(response).filter((item) => (
                !suppressedPersonalSuggestionKeysRef.current.has(normalizeHomeChatSuggestion(item.text))
            ));
            const nextPersonalSuggestions = suggestion.kind === 'personal'
                ? removeMatchingHomePersonalSuggestion(
                    responsePersonalSuggestions,
                    suggestion.text,
                )
                : responsePersonalSuggestions;
            const responseDismissedKeys = resolveDismissedPublicExampleKeys(
                response,
                dismissedKeysAtDispatch,
            );
            const nextDismissedKeys = sanitizeDismissedPublicExampleKeys([
                ...dismissedKeysAtDispatch,
                ...responseDismissedKeys,
                ...(suggestion.kind === 'default' ? [suggestion.publicExampleKey] : []),
            ]);
            const nextPersonalOffset = preservedHomePersonalSuggestionOffset({
                previousSuggestions: personalSuggestions,
                nextSuggestions: nextPersonalSuggestions,
                previousOffset: personalSuggestionOffsetRef.current,
            });
            const nextDefaultOffset = preservedHomeDefaultSuggestionOffset({
                previousPersonalSuggestions: personalSuggestions,
                nextPersonalSuggestions,
                defaults: publicChatExamplesRef.current,
                previousDismissedPublicExampleKeys: dismissedKeysAtDispatch,
                nextDismissedPublicExampleKeys: nextDismissedKeys,
                previousOffset: defaultSuggestionOffsetRef.current,
            });
            personalSuggestionsRef.current = nextPersonalSuggestions;
            setPersonalSuggestions(nextPersonalSuggestions);
            dismissedPublicExampleKeysRef.current = nextDismissedKeys;
            setDismissedPublicExampleKeys(nextDismissedKeys);
            replaceFocusedSuggestion({
                dismissedSuggestion: suggestion,
                nextDefaultOffset,
                nextDismissedPublicExampleKeys: nextDismissedKeys,
                nextPersonalOffset,
                nextPersonalSuggestions,
            });
        } catch (err) {
            if (requestIsCurrent(generation) && suggestionAccountKeyRef.current === requestAccountKey) {
                setErrorMessage(
                    getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) ||
                    'Unable to dismiss suggestion.',
                );
            }
        } finally {
            if (requestIsCurrent(generation) && suggestionAccountKeyRef.current === requestAccountKey) {
                lifecycleRef.current.pending = false;
                setDismissingSuggestionIdentity('');
            }
        }
    };

    const loading = suggestionLoadState.accountKey !== suggestionAccountKey ||
        suggestionLoadState.status !== 'resolved' ||
        (publicChatExamplesLoadState === 'loading' &&
            (authenticated === false || personalSuggestions.length === 0));

    return {
        text, hasText, submitting, errorMessage, loading,
        focusedSuggestions, suggestionActionIsPending, dismissingSuggestionIdentity,
        onChange, onSubmit, startThread, selectFocusedSuggestion, dismissFocusedSuggestion,
        pastChatsClick, prepareSignin,
    };
};

const AskSessionContext = createContext(null);

export const AskSessionProvider = ({ authenticated, uid, children }) => {
    const session = useAskController({ authenticated, uid });
    return <AskSessionContext.Provider value={session}>{children}</AskSessionContext.Provider>;
};

// Standalone route tests and isolated previews can supply their own auth props;
// production always inherits the one app-wide session.
export const AskSessionBoundary = ({ authenticated, uid, children }) => {
    const session = useContext(AskSessionContext);
    return session ? children : (
        <AskSessionProvider authenticated={authenticated} uid={uid}>{children}</AskSessionProvider>
    );
};

export const useAskSession = () => useContext(AskSessionContext);

export default connect((state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
}))(AskSessionProvider);
